import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getDatabase } from 'firebase/database';
import { fetchAndActivate, getRemoteConfig, getValue, isSupported } from 'firebase/remote-config';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Services
export const analytics = typeof window !== 'undefined' ? getAnalytics(app) : null;
export const performance = typeof window !== 'undefined' ? getPerformance(app) : null;
export const database = typeof window !== 'undefined' ? getDatabase(app) : null;

export interface UpdateRemoteConfig {
  targetReleaseId: string | null;
  forceUpdateEnabled: boolean;
  maxUpdateAttempts: number;
  updateRolloutPercent: number;
}

const UPDATE_REMOTE_DEFAULTS: UpdateRemoteConfig = {
  targetReleaseId: null,
  forceUpdateEnabled: true,
  maxUpdateAttempts: 5,
  updateRolloutPercent: 100,
};

const parseBool = (value: string, fallback: boolean): boolean => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

const parseNumber = (value: string, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const getUpdateRemoteConfig = async (): Promise<UpdateRemoteConfig> => {
  if (typeof window === 'undefined') return UPDATE_REMOTE_DEFAULTS;

  try {
    const supported = await isSupported();
    if (!supported) return UPDATE_REMOTE_DEFAULTS;

    const remoteConfig = getRemoteConfig(app);
    remoteConfig.settings = {
      minimumFetchIntervalMillis: 5 * 60 * 1000,
      fetchTimeoutMillis: 10 * 1000,
    };

    // Set local defaults so the app behaves safely before fetch completes.
    remoteConfig.defaultConfig = {
      target_release_id: '',
      force_update_enabled: 'true',
      max_update_attempts: '5',
      update_rollout_percent: '100',
    };

    await fetchAndActivate(remoteConfig);

    const targetReleaseId = getValue(remoteConfig, 'target_release_id').asString().trim() || null;
    const forceUpdateEnabled = parseBool(getValue(remoteConfig, 'force_update_enabled').asString(), true);
    const maxUpdateAttemptsRaw = parseNumber(getValue(remoteConfig, 'max_update_attempts').asString(), 5);
    const updateRolloutPercentRaw = parseNumber(getValue(remoteConfig, 'update_rollout_percent').asString(), 100);

    const maxUpdateAttempts = Math.max(1, Math.min(20, Math.round(maxUpdateAttemptsRaw)));
    const updateRolloutPercent = Math.max(0, Math.min(100, updateRolloutPercentRaw));

    return {
      targetReleaseId,
      forceUpdateEnabled,
      maxUpdateAttempts,
      updateRolloutPercent,
    };
  } catch (error) {
    console.warn('[RemoteConfig] Failed to fetch update config, using defaults.', error);
    return UPDATE_REMOTE_DEFAULTS;
  }
};

/**
 * Utility to log analytics events safely
 */
export const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
};

export default app;
